/**
 * Перетаскивание каналов: по дереву и цели броска считаем новые позиции и
 * родителей, которые уходят в PATCH /guilds/{id}/channels. Чистая функция —
 * без DOM, чтобы её можно было проверить отдельно.
 */
import { ChannelType, type Channel } from '@/types/api'

export type DropTarget =
  | { kind: 'channel'; id: string; where: 'before' | 'after' }
  | { kind: 'category'; id: string; where: 'before' | 'after' | 'inside' }
  | { kind: 'root' }

export interface MoveItem {
  id: string
  position: number
  parent_id?: string | null
}

const byPos = (a: Channel, b: Channel) => (a.position ?? 0) - (b.position ?? 0) || (a.id < b.id ? -1 : 1)

/** null — бросок некуда или ничего не меняет. */
export function planMove(all: Channel[], dragId: string, target: DropTarget): MoveItem[] | null {
  const drag = all.find((c) => c.id === dragId)
  if (!drag) return null
  const categories = all.filter((c) => c.type === ChannelType.GuildCategory).sort(byPos)
  const catIds = new Set(categories.map((c) => c.id))

  if (drag.type === ChannelType.GuildCategory) {
    if (target.kind !== 'category' || target.where === 'inside' || target.id === dragId) return null
    const rest = categories.filter((c) => c.id !== dragId)
    const idx = rest.findIndex((c) => c.id === target.id)
    if (idx === -1) return null
    rest.splice(target.where === 'before' ? idx : idx + 1, 0, drag)
    return renumber(rest, drag.id, undefined)
  }

  let parent: string | null
  let siblings: Channel[]
  let index: number
  const children = (p: string | null) => all.filter((c) => c.type !== ChannelType.GuildCategory && c.id !== dragId && (p ? c.parent_id === p : !c.parent_id || !catIds.has(c.parent_id))).sort(byPos)
  switch (target.kind) {
    case 'channel': {
      const t = all.find((c) => c.id === target.id)
      if (!t || t.id === dragId || t.type === ChannelType.GuildCategory) return null
      parent = t.parent_id && catIds.has(t.parent_id) ? t.parent_id : null
      siblings = children(parent)
      const idx = siblings.findIndex((c) => c.id === t.id)
      index = target.where === 'before' ? idx : idx + 1
      break
    }
    case 'category': {
      if (!catIds.has(target.id)) return null
      parent = target.id
      siblings = children(parent)
      index = 0
      break
    }
    case 'root':
      parent = null
      siblings = children(null)
      index = siblings.length
      break
  }
  siblings.splice(index, 0, drag)
  const currentParent = drag.parent_id && catIds.has(drag.parent_id) ? drag.parent_id : null
  return renumber(siblings, drag.id, currentParent === parent ? undefined : parent)
}

/** Голосовые каналы в порядке списка слева: сначала без категории, затем по категориям. */
export function voiceChannelsInOrder(all: Channel[]): Channel[] {
  const categories = all.filter((c) => c.type === ChannelType.GuildCategory).sort(byPos)
  const rank = new Map(categories.map((c, i) => [c.id, i]))
  const group = (c: Channel) => (c.parent_id ? (rank.get(c.parent_id) ?? -1) : -1)
  return all.filter((c) => c.type === ChannelType.GuildVoice).sort((a, b) => group(a) - group(b) || byPos(a, b))
}

/** Позиции 0..n; в ответ попадают только изменившиеся, перетаскиваемый — всегда (с новым родителем, если сменился). */
function renumber(list: Channel[], dragId: string, newParent: string | null | undefined): MoveItem[] | null {
  const out: MoveItem[] = []
  list.forEach((c, position) => {
    if (c.id === dragId) {
      if ((c.position ?? 0) !== position || newParent !== undefined) out.push(newParent !== undefined ? { id: c.id, position, parent_id: newParent } : { id: c.id, position })
    } else if ((c.position ?? 0) !== position) {
      out.push({ id: c.id, position })
    }
  })
  return out.length ? out : null
}
